import { MagnifyingGlass, X } from "@phosphor-icons/react";
import React, { useEffect, useId, useRef, useState } from "react";
import "./DimensionSearch.css";

interface DimensionSearchProps {
  alwaysOpen?: boolean;
  clearLabel: string;
  direction: "ltr" | "rtl";
  label: string;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}

export default function DimensionSearch({
  alwaysOpen = false,
  clearLabel,
  direction,
  label,
  onChange,
  placeholder,
  value,
}: DimensionSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isExpanded = alwaysOpen || isOpen;
  const inputId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(function () {
    if (!isOpen || alwaysOpen) return;
    inputRef.current?.focus();
  }, [alwaysOpen, isOpen]);

  useEffect(function () {
    if (!isOpen || alwaysOpen) return;

    function handlePointerDown(event: PointerEvent) {
      if (rootRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return function () {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [alwaysOpen, isOpen, value]);

  return (
    <div
      ref={rootRef}
      className={[
        "dimension-search",
        isExpanded ? "is-open" : "",
        alwaysOpen ? "is-always-open" : "",
        value ? "has-value" : "",
      ].filter(Boolean).join(" ")}
      dir={direction}
      role="search"
      onBlur={function (event) {
        if (alwaysOpen) return;
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setIsOpen(false);
      }}
    >
      <button
        type="button"
        className="dimension-search__toggle"
        aria-label={label}
        aria-expanded={isExpanded}
        aria-controls={inputId}
        onClick={function () {
          if (isExpanded) {
            inputRef.current?.focus();
            return;
          }
          setIsOpen(true);
        }}
      >
        <MagnifyingGlass
          size={20}
          weight="bold"
          aria-hidden="true"
        />
      </button>
      <input
        ref={inputRef}
        id={inputId}
        className="dimension-search__input"
        type="text"
        dir="ltr"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        aria-hidden={!isExpanded}
        autoComplete="off"
        spellCheck={false}
        tabIndex={isExpanded ? 0 : -1}
        onChange={function (event) {
          onChange(event.target.value);
        }}
        onKeyDown={function (event) {
          if (event.key !== "Escape") return;
          if (value) {
            onChange("");
            return;
          }
          if (alwaysOpen) {
            event.currentTarget.blur();
            return;
          }
          setIsOpen(false);
          event.currentTarget.blur();
        }}
      />
      <button
        type="button"
        className="dimension-search__clear"
        aria-label={clearLabel}
        aria-hidden={!isExpanded || !value}
        tabIndex={isExpanded && value ? 0 : -1}
        onClick={function () {
          onChange("");
          inputRef.current?.focus();
        }}
      >
        <X
          size={17}
          weight="bold"
          aria-hidden="true"
        />
      </button>
    </div>
  );
}
